import {
  METRIC_AGGREGATIONS,
  METRIC_FILTER_OPS,
  METRIC_FORMATS,
  METRIC_SOURCE_TYPES,
} from "../../../constants/reportBuilder.js";
import { metricDefaultAlias, metricIsComplete, metricOutputName } from "../../../logic/services/reportBuilderMetrics.js";
import { rbInputStyle, rbSelectStyle } from "../reportBuilderTheme.js";
import { Ico } from "../../../components/icons/icons.jsx";

function lbl(c) {
  return { display: "block", fontSize: 10, fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", color: c.sub, marginBottom: 5 };
}

function iconBtn(c, { danger = false } = {}) {
  return {
    width: 30,
    height: 30,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: `1px solid ${c.cardBdr}`,
    borderRadius: 7,
    background: c.card,
    color: danger ? "#EF4444" : c.sub,
    cursor: "pointer",
    flexShrink: 0,
  };
}

export function MetricCard({
  metric,
  index,
  total,
  fields,
  onChange,
  onRemove,
  onClone,
  onMoveUp,
  onMoveDown,
  canRemove,
  c,
  A,
}) {
  const focus = (e) => (e.currentTarget.style.borderColor = `${A}66`);
  const blur = (e) => (e.currentTarget.style.borderColor = c.cardBdr);
  const complete = metricIsComplete(metric);
  const previewAlias = metricOutputName(metric);
  const showPercentile = metric.agg === "PERCENTILE";
  const showField = metric.sourceType !== "expression";
  const showExpression = metric.sourceType === "expression";
  const filter = metric.filter ?? { field: "", op: "=", value: "" };

  return (
    <div
      style={{
        borderRadius: 12,
        border: `1px solid ${complete ? c.divider : "#EF444444"}`,
        background: metric.enabled === false ? c.hover : c.card,
        opacity: metric.enabled === false ? 0.65 : 1,
        overflow: "hidden",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "10px 12px",
          borderBottom: `1px solid ${c.divider}`,
          background: c.hover,
        }}
      >
        <span style={{ fontSize: 11, fontWeight: 700, color: c.sub, width: 18 }}>{index + 1}</span>
        <span style={{ fontSize: 12, fontWeight: 700, color: c.text, flex: 1, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}>
          {complete ? previewAlias : "Incomplete metric"}
        </span>
        <label style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11, color: c.mid, cursor: "pointer" }}>
          <input type="checkbox" checked={metric.enabled !== false} onChange={(e) => onChange({ enabled: e.target.checked })} />
          On
        </label>
        <button type="button" onClick={onMoveUp} disabled={index === 0} style={{ ...iconBtn(c), opacity: index === 0 ? 0.35 : 1 }} aria-label="Move up">
          ↑
        </button>
        <button type="button" onClick={onMoveDown} disabled={index === total - 1} style={{ ...iconBtn(c), opacity: index === total - 1 ? 0.35 : 1 }} aria-label="Move down">
          ↓
        </button>
        <button type="button" onClick={onClone} style={iconBtn(c)} aria-label="Duplicate metric">
          ⧉
        </button>
        {canRemove ? (
          <button type="button" onClick={onRemove} style={iconBtn(c, { danger: true })} aria-label="Remove metric">
            {Ico.x}
          </button>
        ) : null}
      </div>

      <div style={{ padding: 12, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <label style={{ gridColumn: "1 / -1" }}>
          <span style={lbl(c)}>Source type</span>
          <select value={metric.sourceType ?? "field"} onChange={(e) => onChange({ sourceType: e.target.value })} style={rbSelectStyle(c)}>
            {METRIC_SOURCE_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </label>

        {showField ? (
          <label>
            <span style={lbl(c)}>Field</span>
            <select value={metric.field ?? ""} onChange={(e) => onChange({ field: e.target.value })} style={rbSelectStyle(c)} onFocus={focus} onBlur={blur}>
              <option value="">{metric.agg === "COUNT" ? "* (all rows)" : "Select field"}</option>
              {fields.map((f) => (
                <option key={f} value={f}>
                  {f}
                </option>
              ))}
            </select>
          </label>
        ) : null}

        {showExpression ? (
          <label style={{ gridColumn: showField ? undefined : "1 / -1" }}>
            <span style={lbl(c)}>Expression</span>
            <input
              value={metric.expression ?? ""}
              onChange={(e) => onChange({ expression: e.target.value })}
              placeholder="distance_km / NULLIF(fuel_used, 0)"
              style={rbInputStyle(c, false, { mono: true })}
            />
          </label>
        ) : null}

        <label>
          <span style={lbl(c)}>Aggregation</span>
          <select value={metric.agg ?? "SUM"} onChange={(e) => onChange({ agg: e.target.value })} style={rbSelectStyle(c)}>
            {METRIC_AGGREGATIONS.map((a) => (
              <option key={a.value} value={a.value}>
                {a.label}
              </option>
            ))}
          </select>
        </label>

        {showPercentile ? (
          <label>
            <span style={lbl(c)}>Percentile</span>
            <select value={metric.percentile ?? 50} onChange={(e) => onChange({ percentile: Number(e.target.value) })} style={rbSelectStyle(c)}>
              {[50, 75, 90, 95, 99].map((p) => (
                <option key={p} value={p}>
                  p{p}
                </option>
              ))}
            </select>
          </label>
        ) : null}

        <label>
          <span style={lbl(c)}>Output alias</span>
          <input
            value={metric.alias ?? ""}
            onChange={(e) => onChange({ alias: e.target.value })}
            placeholder={metricDefaultAlias(metric)}
            style={rbInputStyle(c, false, { mono: true })}
          />
        </label>

        <label>
          <span style={lbl(c)}>Display format</span>
          <select value={metric.format ?? "auto"} onChange={(e) => onChange({ format: e.target.value })} style={rbSelectStyle(c)}>
            {METRIC_FORMATS.map((f) => (
              <option key={f.value} value={f.value}>
                {f.label}
              </option>
            ))}
          </select>
        </label>

        <label>
          <span style={lbl(c)}>Sort results</span>
          <select value={metric.sort ?? ""} onChange={(e) => onChange({ sort: e.target.value })} style={rbSelectStyle(c)}>
            <option value="">None</option>
            <option value="asc">Ascending</option>
            <option value="desc">Descending</option>
          </select>
        </label>

        <div style={{ gridColumn: "1 / -1", padding: 10, borderRadius: 8, background: c.hover, border: `1px solid ${c.divider}` }}>
          <span style={lbl(c)}>Conditional aggregation (FILTER)</span>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 88px 1fr", gap: 8, alignItems: "end" }}>
            <select
              value={filter.field ?? ""}
              onChange={(e) => onChange({ filter: { ...filter, field: e.target.value } })}
              style={rbSelectStyle(c)}
            >
              <option value="">No filter</option>
              {fields.map((f) => (
                <option key={f} value={f}>
                  {f}
                </option>
              ))}
            </select>
            <select
              value={filter.op ?? "="}
              onChange={(e) => onChange({ filter: { ...filter, op: e.target.value } })}
              style={rbSelectStyle(c)}
              disabled={!filter.field}
            >
              {METRIC_FILTER_OPS.map((op) => (
                <option key={op.value} value={op.value}>
                  {op.label}
                </option>
              ))}
            </select>
            <input
              value={filter.value ?? ""}
              onChange={(e) => onChange({ filter: { ...filter, value: e.target.value } })}
              placeholder="Filter value"
              style={rbInputStyle(c)}
              disabled={!filter.field || filter.op === "null" || filter.op === "notNull"}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
